/**
 * @author        tae@zumper.com (Tae Yoon)
 * @copyright     Copyright (c) 2019, Zumper
 * @description   media bizo selectors
 */

import type { Media } from '@zcode/bizos'
import { buildStaticImageUrl } from '@zcode/nr-utils'
import {
  selectMatterportMaxResImage,
  selectMatterportStandardImage,
  selectMatterportThumbnail,
  selectMatterportUrl,
} from './matterport'
import { selectPanoskinUrl } from './panoskin'
import { selectReelImage, selectReelUrl } from './reels'
import {
  selectVimeoMaxResImage,
  selectVimeoStandardImage,
  selectVimeoThumbnail,
  selectVimeoUrl,
} from './vimeo'
import {
  selectYoutubeEmbedUrl,
  selectYoutubeHqImage,
  selectYoutubeMaxResImage,
  selectYoutubeMqImage,
  selectYoutubeStandardImage,
  selectYoutubeThumbnail,
} from './youtube'

// Video
export const selectVideoUrl = (media: Media | undefined) => {
  // Youtube
  const youtube = selectYoutubeEmbedUrl(media)
  if (youtube) {
    return youtube
  }

  // Vimeo
  const vimeo = selectVimeoUrl(media)
  if (vimeo) {
    return vimeo
  }

  // Generic
  const { isVideo, originUrl } = media || {}

  if (isVideo && originUrl) {
    return originUrl
  }

  return null
}

export const selectVideoThumbnail = (media: Media | undefined) => {
  // Youtube
  const youtube = selectYoutubeThumbnail(media)
  if (youtube) {
    return youtube
  }

  // Vimeo
  const vimeo = selectVimeoThumbnail(media)
  if (vimeo) {
    return vimeo
  }

  // Generic
  return selectVideoUrl(media)
    ? buildStaticImageUrl({ url: 'img/shared/carousel/generic_panorama.jpg', h: 66, w: 66 })
    : null
}

export const selectVideoStandardImage = (media: Media | undefined) => {
  // Youtube
  const youtube = selectYoutubeStandardImage(media)
  if (youtube) {
    return youtube
  }

  // Vimeo
  const vimeo = selectVimeoStandardImage(media)
  if (vimeo) {
    return vimeo
  }

  // Generic
  return selectVideoUrl(media)
    ? buildStaticImageUrl({ url: 'img/shared/carousel/generic_panorama.jpg', h: 480, w: 640 })
    : null
}

/**
 * Not **really** deprecated, but be very careful. Youtube doesn't have "max res"
 * images for every video, for example:
 *
 * 1. "HQ" works: https://img.youtube.com/vi/HccXNpVwXPo/hqdefault.jpg
 * 2. "Max Res" doesn't: https://img.youtube.com/vi/HccXNpVwXPo/maxresdefault.jpg
 *
 * @param media
 * @returns
 * @deprecated `selectVideoMaxishResImage` is more likely to work
 */
export const selectVideoMaxResImage = (media: Media | undefined) => {
  // Youtube
  const youtube = selectYoutubeMaxResImage(media)
  if (youtube) {
    return youtube
  }

  // Vimeo
  const vimeo = selectVimeoMaxResImage(media)
  if (vimeo) {
    return vimeo
  }

  // Generic
  return selectVideoUrl(media)
    ? buildStaticImageUrl({ url: 'img/shared/carousel/generic_panorama.jpg', h: 960, w: 1280 })
    : null
}

export const selectVideoMaxishResImage = (media: Media | undefined) => {
  // Youtube
  const youtube = selectYoutubeHqImage(media)
  if (youtube) {
    return youtube
  }

  // Vimeo
  const vimeo = selectVimeoMaxResImage(media)
  if (vimeo) {
    return vimeo
  }

  // Generic
  return selectVideoUrl(media)
    ? buildStaticImageUrl({ url: 'img/shared/carousel/generic_panorama.jpg', h: 960, w: 1280 })
    : null
}

export { selectYoutubeHqImage, selectYoutubeMqImage }

// Internal Video
export const selectInternalVideoUrl = (media: Media | undefined) => {
  const reel = selectReelUrl(media?.mediaId)

  return reel
}

export const selectInternalVideoImage = (media: Media | undefined) => {
  const reel = selectReelImage(media?.mediaId)

  return reel
}

// Iframe
export const selectIframeUrl = (media: Media | undefined) => {
  // Matterport
  const matterportUrl = selectMatterportUrl(media)
  if (matterportUrl) {
    return matterportUrl
  }

  // Panoskin
  const panoskinUrl = selectPanoskinUrl(media)
  if (panoskinUrl) {
    return panoskinUrl
  }

  // Generic
  const { isIframe, originUrl } = media || {}

  if (isIframe) {
    return originUrl
  }

  return null
}

export const selectIframeThumbnail = (media: Media | undefined) => {
  // Matterport
  const matterPortThumbnail = selectMatterportThumbnail(media)
  if (matterPortThumbnail) {
    return matterPortThumbnail
  }

  // Generic
  return selectIframeUrl(media)
    ? buildStaticImageUrl({ url: 'img/shared/carousel/generic_panorama.jpg', h: 66, w: 66 })
    : null
}

export const selectIframeStandardImage = (media: Media | undefined) => {
  // Matterport
  const matterPortStandardImage = selectMatterportStandardImage(media)
  if (matterPortStandardImage) {
    return matterPortStandardImage
  }

  // Generic
  return selectIframeUrl(media)
    ? buildStaticImageUrl({ url: 'img/shared/carousel/generic_panorama.jpg', h: 480, w: 640 })
    : null
}

export const selectIframeMaxResImage = (media: Media | undefined) => {
  // Matterport
  const matterPortMaxResImage = selectMatterportMaxResImage(media)
  if (matterPortMaxResImage) {
    return matterPortMaxResImage
  }

  // Generic
  return selectIframeUrl(media)
    ? buildStaticImageUrl({ url: 'img/shared/carousel/generic_panorama.jpg', h: 960, w: 1280 })
    : null
}

export const selectThumbnail = (media: Media | undefined) => {
  if (!media) {
    return null
  }

  const { isIframe, isVideo, url } = media

  if (isIframe) {
    return selectIframeThumbnail(media)
  }

  if (isVideo) {
    return selectVideoThumbnail(media)
  }

  return url
}

export const selectStandardImage = (media: Media | undefined) => {
  if (!media) {
    return null
  }

  const { isIframe, isVideo } = media

  if (isIframe) {
    return selectIframeStandardImage(media)
  }

  if (isVideo) {
    return selectVideoStandardImage(media)
  }

  return media.url
}
